import { AxiosError } from 'axios'
import { useDebouncedCallback } from 'use-debounce'
import { Autocomplete } from '@material-ui/lab'
import { camelCase } from 'lodash'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useState } from 'react'
import * as yup from 'yup'
import { Dock, DockType, Facility } from 'src/api/models'
import { useForm } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import toast from 'react-hot-toast'

import { useQueryPaginator } from 'src/components/Table'

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  hidden: {
    display: 'none',
  },
}))

interface DockProps {
  name: string
  dock_type: DockType // eslint-disable-line camelcase
  start_index: number // eslint-disable-line camelcase
  end_index: number // eslint-disable-line camelcase
}

const dockSchema = yup.object().shape({
  name: yup.string().required().label('Name'),
  dock_type: yup.string().required().oneOf(Object.values(Dock.DockType)).label('Dock Type'),
  start_index: yup.number().optional().label('Start Index'),
  end_index: yup.number().optional().label('End Index'),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  dock?: Dock
  facility?: Facility
}

export const DockDialog = (props: Props) => {
  const { open, handleClose, onSave, update, dock, facility } = props
  const [index, setIndex] = useState(false)
  const classes = useStyles()

  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)
  const dePatchFacilityFilters = useDebouncedCallback(facilityPaginator.patchFilters, 100)

  const onSubmit = (data: DockProps) => {
    let func = skuit.sc.docks.create
    let action = 'creat'
    if (update) {
      func = (data: DockProps) => skuit.sc.docks.patch(dock?.guid || '', data)
      action = 'updat'
    }
    func(data)
      .then((newDock: Dock) => {
        handleClose()
        onSave?.()
        toast.success(() => (
          <Notification
            message={`Successfully ${action}ed Dock: ${newDock.name}`}
            newObject={newDock}
          />
        ))
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Dock')
        setApiErrors(formData.setError, error)
      })
  }

  const { Form, Field, data: formData } = useForm(dockSchema)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{update ? 'Update' : 'Create'} Dock Door</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field
                name="facility"
                defaultValue={dock?.facility || facility}
                getSubmitValue={(facility) => facility.guid}
                renderField={({ name, value, setValue, error }) => (
                  <Autocomplete
                    disabled={!!facility || update}
                    id={name}
                    options={facilityPaginator.data.results}
                    getOptionLabel={(facility) => facility?.name || ''}
                    getOptionSelected={(option, value) => option.guid === value?.guid}
                    filterOptions={(option) => option}
                    onChange={(_: any, newValue: any | null) => {
                      setValue(name, newValue)
                    }}
                    value={value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name={name}
                        label="Facility"
                        error={!!error}
                        helperText={error}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          dePatchFacilityFilters.callback({ search: event.target.value })
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="name"
                defaultValue={dock?.name}
                renderField={({ name, value, setValue, error }) => (
                  <TextField
                    id={name}
                    name={name}
                    label="Name"
                    error={!!error}
                    helperText={error}
                    fullWidth
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if ((e.target.value as string).includes('{index}')) {
                        setIndex(true)
                      } else {
                        setIndex(false)
                      }
                      setValue(name, e.target.value)
                    }}
                    value={value || ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="start_index"
                defaultValue={1}
                renderField={({ name, value, setValue, error }) => (
                  <TextField
                    id={name}
                    className={index ? undefined : classes.hidden}
                    name={name}
                    label="Start Index"
                    type="number"
                    error={!!error}
                    helperText={error}
                    fullWidth
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setValue(name, e.target.value)
                    }
                    value={value || ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="end_index"
                defaultValue={1}
                renderField={({ name, value, setValue, error }) => (
                  <TextField
                    id={name}
                    className={index ? undefined : classes.hidden}
                    name={name}
                    label="End Index"
                    type="number"
                    error={!!error}
                    helperText={error}
                    fullWidth
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setValue(name, e.target.value)
                    }
                    value={value || ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="dock_type"
                defaultValue={dock?.dockType}
                getSubmitValue={(value) => value}
                renderField={({ name, value, setValue, error }) => (
                  <FormControl fullWidth>
                    <InputLabel id={name}>Dock Type</InputLabel>
                    <Select
                      id={name}
                      name={name}
                      label="Type"
                      error={!!error}
                      onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                        setValue(name, event.target.value as string)
                      }}
                      value={value || ''}
                    >
                      {Object.entries(Dock.DockType).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {camelCase(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error={!!error}>{error}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
